import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Check, AlertCircle, Loader2, ShoppingBag, ArrowLeft } from 'lucide-react';
import api from '../../services/api';

export const OrderConfirmation = ({ onReset }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed, cancelled
  const [order, setOrder] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const cancelled = searchParams.get('cancelled');
  
  useEffect(() => {
    if (cancelled === 'true') {
      setStatus('cancelled');
      return;
    }
    
    if (!sessionId || !orderId) {
      return;
    }
    
    const pollPaymentStatus = async () => {
      try {
        const result = await api.getCheckoutStatus(sessionId);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          // Fetch order details
          const orderData = await api.getOrder(orderId);
          setOrder(orderData);
          // Clear URL params
          setSearchParams({});
        } else if (result.status === 'expired') {
          setStatus('failed');
          setSearchParams({});
        } else if (pollCount < 10) {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('failed');
          setSearchParams({});
        }
      } catch (error) {
        if (pollCount < 5) {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };
    
    pollPaymentStatus();
  }, [sessionId, orderId, cancelled, pollCount, setSearchParams]);
  
  const handleBackToDesigner = () => {
    setSearchParams({});
    if (onReset) onReset();
  };
  
  if (status === 'checking') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6">
        <Loader2 className="w-16 h-16 text-primary animate-spin mb-6" />
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
          Verifying Payment
        </h1>
        <p className="text-gray-500 text-center">
          Please wait while we confirm your payment...
        </p>
      </div>
    );
  }
  
  if (status === 'cancelled') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6">
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
          <AlertCircle className="w-10 h-10 text-yellow-600" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
          Payment Cancelled
        </h1>
        <p className="text-gray-500 text-center mb-8">
          Your payment was cancelled. Your design is still saved.
        </p>
        <Button
          data-testid="back-to-designer-btn"
          onClick={handleBackToDesigner}
          className="rounded-full px-8 h-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Designer
        </Button>
      </div>
    );
  }
  
  if (status === 'failed') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <AlertCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
          Payment Failed
        </h1>
        <p className="text-gray-500 text-center mb-8">
          Something went wrong with your payment. Please try again.
        </p>
        <Button
          data-testid="try-again-btn"
          onClick={handleBackToDesigner}
          className="rounded-full px-8 h-12"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }
  
  // Success state
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50 p-6">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-8 animate-fadeIn">
        <Check className="w-12 h-12 text-green-600" />
      </div>
      
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center animate-slideUp">
        Order Confirmed!
      </h1>
      
      <p className="text-gray-500 text-center mb-8 animate-slideUp">
        Thank you for your order. We&apos;ve sent a confirmation email.
      </p>
      
      {order && (
        <div className="bg-white rounded-3xl shadow-floating p-6 w-full max-w-md mb-8 animate-slideUp">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingBag className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-lg">Order Details</h2>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Order ID</span>
              <span className="font-mono text-xs">{order.id?.slice(0, 8)}...</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Name</span>
              <span className="font-medium">{order.full_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Size</span>
              <span className="font-medium">{order.tshirt_size}</span>
            </div>
            <div className="flex justify-between text-sm items-center">
              <span className="text-gray-500">Color</span>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full border border-gray-200"
                  style={{ backgroundColor: order.tshirt_color }}
                />
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Print</span>
              <span className="font-medium capitalize">{order.print_placement}</span>
            </div>
            <div className="border-t border-gray-100 pt-3 flex justify-between">
              <span className="font-semibold">Total Paid</span>
              <span className="font-bold text-primary">${order.total_price?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
      
      <Button
        data-testid="create-new-design-btn"
        onClick={handleBackToDesigner}
        className="rounded-full px-8 h-14 bg-primary hover:bg-primary/90 text-white font-bold shadow-button btn-scale"
      >
        Create New Design
      </Button>
    </div>
  );
};

export default OrderConfirmation;
